import type { Block, DesignSystemDto } from '@ads/shared';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Doc } from '../doc/Doc';

/* ─────────────────────────────────────────────────────────────────────
   O palco de um documento: a página tem largura de página (760px) e
   encolhe para caber, sem refluir o texto. Enquanto o agente escreve,
   acompanha o fim; quando a conversa muda um bloco, vai até ele.
   ───────────────────────────────────────────────────────────────────── */

export interface PaperStageProps {
  title: string;
  org: string;
  deliverable: string;
  date: string;
  blocks: Block[];
  ds: DesignSystemDto | null | undefined;
  liveFrom?: number;
  fresh?: readonly number[];
  /** o agente está escrevendo a peça (geração) */
  writing?: boolean;
  /** capa pronta, blocos ainda por vir */
  pending?: boolean;
  /** o fim acompanha a escrita */
  follow?: boolean;
  children?: ReactNode;
}

export function PaperStage(p: PaperStageProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const [zoom, setZoom] = useState(1);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const fit = () => setZoom(Math.min(1, (el.clientWidth - 48) / 760));
    fit();
    const ro = new ResizeObserver(fit); ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* acompanha a escrita, a menos que a pessoa tenha subido para reler */
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !following.current || !p.follow) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [p.blocks.length, p.follow]);

  /* a conversa mudou um bloco: o palco vai até o mais recente */
  const last = p.fresh?.length ? p.fresh[p.fresh.length - 1]! : null;
  useEffect(() => {
    if (last === null) return;
    const el = scroller.current?.querySelector(`[data-b="${last}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [last, p.blocks]);

  return (
    <div className="gen-stage">
      <div className="gen-scroll" ref={scroller}
        onScroll={e => { const el = e.currentTarget; following.current = el.scrollHeight - el.scrollTop - el.clientHeight < 140; }}>
        <div className="gen-paper" style={{ zoom }}>
          <Doc title={p.title} org={p.org} deliverable={p.deliverable} date={p.date} blocks={p.blocks} ds={p.ds}
            liveFrom={p.liveFrom} fresh={p.fresh} writing={p.writing} pending={p.pending} />
          {p.children}
        </div>
      </div>
    </div>
  );
}
